/**
 * BottomDock — floating breadcrumb navigation bar.
 *
 * Reads the current React Router path to build a location trail.
 * Dashboard is always one tap away. Shows step progress inside the
 * Report Generator when a step context is provided.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { patients as patientsApi } from '../api/endpoints';

interface BreadcrumbSegment {
  label: string;
  path: string;
  isCurrent: boolean;
}

/** Map well-known route segments to human-readable labels. */
const SEGMENT_LABELS: Record<string, string> = {
  assessment: 'Assessment',
  'language-sample': 'Language Sample',
  literacy: 'Literacy Program',
  lesson: 'Lesson',
  progress: 'Progress',
  dashboard: 'Dashboard',
  'report-generator': 'Report Generator',
  new: 'New Report',
  settings: 'Settings',
  subscription: 'Subscription',
  admin: 'Admin',
  therapy: 'Sessions',
  inbox: 'Inbox',
};

export default function BottomDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState<string | null>(null);
  const [patientIdInPath, setPatientIdInPath] = useState<string | null>(null);

  const pathname = location.pathname;

  // Extract patientId from known route patterns
  useEffect(() => {
    const assessmentMatch = pathname.match(/^\/assessment\/([^/]+)/);
    const reportMatch = pathname.match(/^\/report-generator\/([^/]+)/);
    const therapyMatch = pathname.match(/^\/therapy\/([^/]+)/);
    const id = assessmentMatch?.[1] || reportMatch?.[1] || (therapyMatch?.[1] !== 'inbox' ? therapyMatch?.[1] : null);

    if (id && id !== 'new') {
      setPatientIdInPath(id);
    } else {
      setPatientIdInPath(null);
      setPatientName(null);
    }
  }, [pathname]);

  // Fetch patient name when we have a patientId
  useEffect(() => {
    if (!patientIdInPath) return;
    let cancelled = false;
    patientsApi.get(patientIdInPath).then(({ data }) => {
      if (!cancelled && data) {
        setPatientName(`${data.firstName} ${data.lastName}`);
      }
    }).catch(() => {
      // Best-effort — don't block the dock
    });
    return () => { cancelled = true; };
  }, [patientIdInPath]);

  // Build breadcrumb segments from the pathname
  const buildSegments = (): BreadcrumbSegment[] => {
    const segments: BreadcrumbSegment[] = [
      { label: 'Dashboard', path: '/', isCurrent: false },
    ];

    const parts = pathname.replace(/^\//, '').split('/').filter(Boolean);
    let currentPath = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      currentPath += `/${part}`;
      const isLast = i === parts.length - 1;

      // Skip raw patient IDs if we already resolved the name
      if (patientIdInPath && part === patientIdInPath && patientName) {
        segments.push({
          label: patientName,
          path: currentPath,
          isCurrent: isLast,
        });
        continue;
      }

      // Skip unknown UUIDs/IDs that look like patient IDs
      if (patientIdInPath && part === patientIdInPath && !patientName) {
        continue;
      }

      // Skip 'new' if it's the only thing after report-generator
      if (part === 'new' && parts[i - 1] === 'report-generator') {
        // Label the previous segment as "New Report" instead
        if (segments.length > 0) {
          segments[segments.length - 1].label = 'New Report';
          segments[segments.length - 1].isCurrent = isLast;
        }
        continue;
      }

      const label = SEGMENT_LABELS[part] || part;
      segments.push({
        label,
        path: currentPath,
        isCurrent: isLast,
      });
    }

    return segments;
  };

  const segments = buildSegments();

  // Hide dock entirely on root caseload/dashboard — it's redundant there
  const isRootRoute = pathname === '/' || pathname === '';
  if (isRootRoute) return null;

  // Hide dock on report-generator wizard pages — uses static navigation instead
  if (/^\/report-generator\/(new|[^/]+)/.test(pathname)) return null;

  return (
    <Box
      className="easi-no-print"
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 1.25,
        bgcolor: '#1A1D23',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}
    >
      {segments.map((segment, idx) => (
        <Box key={segment.path + idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {idx > 0 && (
            <ChevronRightIcon sx={{ fontSize: 16, color: '#4B5563', flexShrink: 0 }} />
          )}
          <Box
            onClick={() => navigate(segment.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              transition: 'all 150ms ease',
              ...(segment.isCurrent
                ? {
                    bgcolor: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(52, 211, 153, 0.3)',
                  }
                : {
                    bgcolor: idx === 0 ? 'rgba(107, 114, 128, 0.2)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(107, 114, 128, 0.3)',
                    },
                  }),
            }}
          >
            {idx === 0 && (
              <HomeIcon sx={{ fontSize: 16, color: segment.isCurrent ? '#34D399' : '#9CA3AF', flexShrink: 0 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                fontWeight: segment.isCurrent ? 600 : 500,
                color: segment.isCurrent ? '#34D399' : '#9CA3AF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {segment.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
